// Copyright (c) Zammo Inc.
import React from 'react';
import { useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';

import TtsAvatarOnlySimulatorWelcomeModal from './TtsAvatarOnlySimulatorWelcomeModal';

export type TtsAvatarOnlySimulatorProps = {
  zammoApiBaseUrl: string;
  simulatorToken: string;
};

type IceServerInfo = {
  urls: string[];
  username: string;
  password: string;
};

type GetTtsAvatarSessionInfoResponseDto = {
  server: IceServerInfo;
  selectedAvatarCharacter: string;
  selectedAvatarStyle: string;
  speechToken: string;
  speechRegion: string;
  multiModalSessionEndpointUrl: string;
  speechTokenEndpointUrl: string;
};

type GetSimulatorSessionResponse = {
  ttsAvatarSessionInfo: GetTtsAvatarSessionInfoResponseDto;
  zammoUiKitScriptUrl: string;
  ttsAvatarScriptUrl: string;
};

const Container = styled.div`
  font-size: 1rem;

  > div {
    margin-bottom: 1rem;
    line-height: 1.5;
  }
`;

const style = {
  content: {
    bottom: 'auto',
    height: 'auto', // set height
    left: '50%',
    padding: '2rem',
    right: 'auto',
    top: '50%', // start from center
    transform: 'translate(-50%, -50%)', // adjust top "up" based on height
    width: '40%',
    maxWidth: '40rem',
  },
};

const TtsAvatarOnlySimulator = ({ zammoApiBaseUrl, simulatorToken }: TtsAvatarOnlySimulatorProps) => {
  /**                     --------------- STATE ---------------                     **/
  const [ttsAvatarSessionInfo, setTtsAvatarSessionInfo] = useState<GetTtsAvatarSessionInfoResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);

  /**                     --------------- HELPER FUNCTIONS ---------------                     **/
  const initializeTtsAvatarApp = async (ttsAvatarSessionInfoResponseDto: GetTtsAvatarSessionInfoResponseDto) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ttsAvatarApp = (window as any).avatarApp;
    ttsAvatarApp.sessionConfig = ttsAvatarSessionInfoResponseDto;

    ttsAvatarApp.createUI();
    ttsAvatarApp.toggleFullscreen();
    ttsAvatarApp.hideClosingButton();
    ttsAvatarApp.showLoading();

    await ttsAvatarApp.initializeBot({ zammoApiBaseUrl });

    ttsAvatarApp.setupAvatar();
  };

  const initializeTtsAvatarScript = async (ttsAvatarScriptUrl: string) => {
    const existingTtsAvatarScript = document.querySelector('script[id*="AzureTTSAvatar"]');

    if (!existingTtsAvatarScript) {
      const script = document.createElement('script');
      script.id = 'ZammoAzureTTSAvatarScript';
      script.src = `${ttsAvatarScriptUrl}?${new Date().getTime()}`;
      script.async = true;
      script.charset = 'utf-8';

      script.setAttribute('executionType', 'controlled');

      return new Promise<void>((resolve) => {
        script.onload = async () => {
          resolve();
        };

        document.body.appendChild(script);
      });
    }
  };

  const initializeUIKitScript = async (zammoUiKitScriptUrl: string) => {
    const existingZammoScript = document.querySelector('#ZammoBotUIKitScript');

    if (!existingZammoScript) {
      const script = document.createElement('script');
      script.id = 'ZammoBotUIKitScript';
      script.src = `${zammoUiKitScriptUrl}?${new Date().getTime()}`;
      script.async = true;

      script.setAttribute('zammoApiBaseUrl', zammoApiBaseUrl);
      script.setAttribute('executionType', 'controlled');

      return new Promise<void>((resolve) => {
        script.onload = async () => {
          resolve();
        };

        document.body.appendChild(script);
      });
    }
  };

  const initializeSimulator = async () => {
    try {
      setIsLoading(true);
      const getSimulatorSessionResponse = await fetch(
        `${zammoApiBaseUrl}/api/azure-tts-avatar/simulator/session?token=${simulatorToken}`
      );
      const getSimulatorSessionResponseAsJson =
        (await getSimulatorSessionResponse.json()) as GetSimulatorSessionResponse;
      setTtsAvatarSessionInfo(getSimulatorSessionResponseAsJson.ttsAvatarSessionInfo);
      await initializeUIKitScript(getSimulatorSessionResponseAsJson.zammoUiKitScriptUrl);
      await initializeTtsAvatarScript(getSimulatorSessionResponseAsJson.ttsAvatarScriptUrl);
      setIsLoading(false);
      await initializeTtsAvatarApp(getSimulatorSessionResponseAsJson.ttsAvatarSessionInfo);
    } catch (initializeErr) {
      // eslint-disable-next-line no-console
      console.error(initializeErr);
      setIsLoading(false);
      setShowFailedModal(true);
    }
  };

  /**                     --------------- LOAD DATA ---------------                     **/

  /**                     --------------- UI COMPONENT ---------------                     **/
  return (
    <>
      {!isLoading && !ttsAvatarSessionInfo && !showFailedModal && (
        <TtsAvatarOnlySimulatorWelcomeModal isOpen={true} onConfirm={initializeSimulator} />
      )}
      {showFailedModal && (
        <Modal isOpen={showFailedModal} style={style}>
          <Container>
            <div>
              The TTS Avatar experience could not be loaded. Ensure that the experience has been configured with your
              Zammo representative
            </div>
          </Container>
        </Modal>
      )}
    </>
  );
};

export default TtsAvatarOnlySimulator;
